import { useCallback, useEffect, useState } from 'react';
import { HandMetal, Loader2, ArrowUpCircle, Check } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import api from '@/lib/api';

/**
 * PassQuotaCard — Profile-page card showing the user's daily Pass usage,
 * lifetime received count, and a button to request a higher daily limit.
 *
 * Kept as a self-contained card so it can be dropped into Profile.jsx
 * (and later moved elsewhere if the Profile grows crowded).
 */
export default function PassQuotaCard() {
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestOpen, setRequestOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [requestedLimit, setRequestedLimit] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchQuota = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/pass/quota');
      setQuota(data);
    } catch (_) {
      setQuota(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuota();
  }, [fetchQuota]);

  const submitRequest = async () => {
    setSubmitting(true);
    try {
      const body = {};
      if (reason.trim()) body.reason = reason.trim();
      const parsed = parseInt(requestedLimit, 10);
      if (!Number.isNaN(parsed) && parsed > 0) body.requested_limit = parsed;
      await api.post('/pass/request-increase', body);
      toast.success('Request submitted — we\u2019ll review it soon');
      setRequestOpen(false);
      setReason('');
      setRequestedLimit('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const usagePct = quota
    ? Math.min(100, Math.round((quota.used_today / Math.max(1, quota.daily_limit)) * 100))
    : 0;

  return (
    <div
      className="mt-6 rounded-xl border border-border bg-card/40 p-3 sm:p-4"
      data-testid="pass-quota-card"
    >
      <div className="flex items-start gap-2.5">
        <HandMetal className="w-4 h-4 mt-0.5 text-[hsl(var(--primary))] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-sm font-medium text-foreground">Pass activity</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setRequestOpen(true)}
              className="h-7 text-[11px] rounded-lg"
              data-testid="pass-request-increase-button"
            >
              <ArrowUpCircle className="w-3 h-3 mr-1" />
              Request higher limit
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Pass tracks + crates to fellow GroovLensers via QR. Each send counts
            toward your daily limit. Received tracks don&apos;t count.
          </p>

          {/* Stats strip */}
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-background border border-border py-2 px-1.5">
              <div className="text-lg font-display font-semibold text-foreground tabular-nums">
                {loading ? '\u2014' : `${quota?.used_today ?? 0}/${quota?.daily_limit ?? 0}`}
              </div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
                Used today
              </div>
            </div>
            <div className="rounded-lg bg-background border border-border py-2 px-1.5">
              <div className="text-lg font-display font-semibold text-foreground tabular-nums">
                {loading ? '\u2014' : (quota?.remaining_today ?? 0)}
              </div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
                Remaining
              </div>
            </div>
            <div className="rounded-lg bg-background border border-border py-2 px-1.5">
              <div className="text-lg font-display font-semibold text-[hsl(var(--primary))] tabular-nums">
                {loading ? '\u2014' : (quota?.received_all_time ?? 0)}
              </div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
                Received
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-3">
            <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
              <div
                className="h-full bg-[hsl(var(--primary))] transition-all duration-300"
                style={{ width: `${usagePct}%` }}
              />
            </div>
            {quota?.resets_in_seconds != null && (
              <div className="text-[10px] font-mono text-muted-foreground mt-1">
                Resets in ~{Math.max(1, Math.round(quota.resets_in_seconds / 3600))}h
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Request increase modal */}
      <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
        <DialogContent className="max-w-md" data-testid="pass-request-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <ArrowUpCircle className="w-4 h-4 text-[hsl(var(--primary))]" />
              Request a higher Pass limit
            </DialogTitle>
            <DialogDescription className="text-xs">
              Your current daily limit is <span className="font-mono">{quota?.daily_limit ?? 50}</span>.
              Tell us why you need more and we&apos;ll review it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="requested-limit" className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
                Requested daily limit
              </Label>
              <Input
                id="requested-limit"
                type="number"
                min={1}
                max={1000}
                value={requestedLimit}
                onChange={(e) => setRequestedLimit(e.target.value)}
                placeholder={`e.g. ${(quota?.daily_limit ?? 50) + 50}`}
                className="mt-1 h-9 text-sm"
                data-testid="pass-request-limit-input"
              />
            </div>
            <div>
              <Label htmlFor="request-reason" className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
                Reason (optional)
              </Label>
              <Textarea
                id="request-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 280))}
                placeholder="e.g. Running a festival booth this weekend, expecting lots of in-person Passes"
                rows={3}
                className="mt-1 text-xs resize-none"
                data-testid="pass-request-reason-input"
              />
              <div className="text-right text-[10px] font-mono text-muted-foreground tabular-nums mt-0.5">
                {reason.length}/280
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={submitRequest}
              disabled={submitting}
              data-testid="pass-request-submit"
            >
              {submitting ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Check className="w-3.5 h-3.5 mr-1.5" />
              )}
              Submit request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
